import axios from "../axios";
import { getUser } from "./api/user.api";
import { setIsLoginLoading } from "../features/common/slices/common.slice";
import { login } from "../features/auth/slices/auth.slice";
import { setAccounts } from "../features/accounts/slices/account.slice";
import { addNotification } from "../features/notifications/slices/notification.slice";
import { setRoleName } from "../features/auth/slices/permission.slice";
import { logErrorToSentry } from "../utilis/sentry-logger";

export const redirectAuth0 = (domain, clientId, redirectUri, email, stateValue) => {
    if (!domain || !clientId || !redirectUri) return;

    const scope = "openid profile email";

    const authUrl = `https://${domain}/authorize` +
        `?response_type=code` +
        `&client_id=${clientId}` +
        `&redirect_uri=${redirectUri}` +
        `&scope=${encodeURIComponent(scope)}` +
        `&prompt=login` +
        `&login_hint=${encodeURIComponent(email)}` +
        `&state=${encodeURIComponent(stateValue)}`;

    window.location.href = authUrl;
};

export const handleAuthToken = async (dispatch, setJwt, navigate, triggerDashboardTutorial) => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const error = params.get('error');
    const infoMsg = params.get('message');

    if (token || error) {
        if (error) {
            const decodedErrorMessage = decodeURIComponent(infoMsg);

            dispatch(addNotification({
                type: "error",
                label: decodedErrorMessage || "Authentication failed, please try again.",
                id: "updateUserError",
            }));

            return;
        }

        if (token) {
            try {
                dispatch(setIsLoginLoading(true));
                axios.defaults.headers.common['x-auth'] = token;
                sessionStorage.setItem('x-auth', token);
                const res = await getUser();
                if (res && res.user) {
                    if (!!infoMsg) {
                        dispatch(addNotification({
                            type: "success",
                            label: decodeURIComponent(infoMsg),
                            id: "updateUserError",
                        }));
                    }

                    const user = res.user;
                    localStorage.setItem("lastLoginEmail", user?.email);
                    await dispatch(login(user));
                    await dispatch(setRoleName(user?.role?.slug));
                    // await getAccount(user, isMobile);
                    await setJwt(token);
                    dispatch(setAccounts("refreshing"));
                    if (!user?.requirePasswordReset) {
                        await triggerDashboardTutorial();
                    }
                    // Navigate directly to dashboard
                    navigate('/dashboard/hospital');
                }
            } catch (error) {
                logErrorToSentry(error, {
                    service: 'login',
                    event: 'handle_auth_token_error',
                    extra: {
                        hasToken: !!token,
                        hasError: !!error,
                        statusCode: error?.response?.status,
                        errorMessage: error?.response?.data?.message
                    }
                });
                dispatch(setIsLoginLoading(false));
                dispatch(addNotification({
                    type: "error",
                    label: "Authentication failed, please try again.",
                    id: "authFailedError",
                }));
                return navigate("/login");
            }
        }
    }
}

export const handleTenantFetchByEmail = async ({
    email,
    validateEmail,
    dispatch,
    setEmailError,
    setButtonAuth0Text,
    setLoginRedirectProcessed,
    setTenants,
    setShowTenantSelection,
    setSelectedTenant,
    handleTenantRedirect,
}) => {
    if (!validateEmail(email)) {
        setEmailError('Invalid email address');
        dispatch(addNotification({
            type: 'error',
            label: 'Invalid email address',
            id: 'invalidEmailError',
        }));
        setButtonAuth0Text('Log in to SNF Data');
        setLoginRedirectProcessed(false);
        return;
    }

    try {
        const baseUrl = process.env.REACT_APP_TENANT_API_BASE_URL;
        const emailEncoded = encodeURIComponent(email);
        const response = await axios.get(`${baseUrl}/tenants/by-email/${emailEncoded}`);
        const userTenants = response.data;

        if (userTenants.length === 1) {
            handleTenantRedirect(userTenants[0]);
            return;
        }

        setTenants(userTenants);
        setShowTenantSelection(true);
        setButtonAuth0Text("Log In to SNF Data");
        setSelectedTenant(userTenants[0]);
        return true;

    } catch (error) {
        logErrorToSentry(error, {
            service: 'login',
            event: 'fetch_tenant_info_error',
            extra: {
                email,
                baseUrl: process.env.REACT_APP_TENANT_API_BASE_URL,
                statusCode: error?.response?.status,
                errorMessage: error?.response?.data?.message
            }
        });
        return false;
    }
}